import * as ents from '@attain-apps/entities'

export namespace Table {
  const ROW_INDENT_WIDTH_PX = 20

  /** Resolved column data consumed by the TreeTable template. */
  export interface ColumnViewModel {
    readonly id: ents.core.display.Table.Column.Id
    readonly name: string
    readonly valueInfo?: ents.core.common.value.Info
  }

  /** Cell data pre-normalized for template binding. */
  export interface CellViewModel {
    readonly columnId: ents.core.display.Table.Column.Id
    readonly tooltip: string
    readonly value?: ents.core.common.value.Raw
    readonly valueInfo?: ents.core.common.value.Info
  }

  /** Tree row projected into the visible, expandable table shape. */
  export interface VisibleRow {
    readonly cells: readonly CellViewModel[]
    readonly expanded: boolean
    readonly hasChildren: boolean
    readonly indentPx: number
    readonly rowId?: ents.core.display.Table.Tree.Row.Id
    readonly trackId: string
  }

  /** Resolves column display names and value metadata from the DMS schema. */
  export function getColumnViewModels(
    columns: readonly ents.core.display.Table.Column[],
    schema: ents.core.dms.models.Schema,
  ): readonly ColumnViewModel[] {
    return columns.map(column => {
      const metadata = ents.core.display.Field.Metadata.Source.getMetadata(
        column,
        schema,
      )
      const type = metadata?.type
        ? ents.core.dms.data.Value.Type.toCommonValueType(metadata.type, schema)
        : undefined

      return {
        id: column.id,
        name: metadata?.name ?? column.id,
        valueInfo: type ? { type } : undefined,
      }
    })
  }

  /** Builds the expanded tree-table rows consumed directly by the template. */
  export function getVisibleRows(
    rows: readonly ents.core.display.Table.Tree.Row[],
    columns: readonly ColumnViewModel[],
    rootId: ents.core.display.Table.Tree.Row.Id | undefined,
    expandedRowIds: ReadonlySet<ents.core.display.Table.Tree.Row.Id>,
  ): readonly VisibleRow[] {
    const rowsByParentId = getRowsByParentId(rows)
    const rootRows = getRootRows(rows, rowsByParentId, rootId)
    const visibleRows: VisibleRow[] = []
    const activePath = new Set<ents.core.display.Table.Tree.Row.Id>()

    for (const row of rootRows) {
      appendVisibleRow(
        row,
        0,
        columns,
        rowsByParentId,
        expandedRowIds,
        visibleRows,
        activePath,
      )
    }

    return visibleRows
  }

  /** Appends a row and, when expanded, recursively appends visible children. */
  function appendVisibleRow(
    row: ents.core.display.Table.Tree.Row,
    level: number,
    columns: readonly ColumnViewModel[],
    rowsByParentId: ReadonlyMap<
      ents.core.display.Table.Tree.Row.Id | undefined,
      readonly ents.core.display.Table.Tree.Row[]
    >,
    expandedRowIds: ReadonlySet<ents.core.display.Table.Tree.Row.Id>,
    visibleRows: VisibleRow[],
    activePath: Set<ents.core.display.Table.Tree.Row.Id>,
  ): void {
    const children =
      row.id !== undefined ? (rowsByParentId.get(row.id) ?? []) : []
    const expanded = row.id !== undefined && expandedRowIds.has(row.id)

    visibleRows.push({
      cells: columns.map(column => getCellViewModel(row, column)),
      expanded,
      hasChildren: children.length > 0,
      indentPx: level * ROW_INDENT_WIDTH_PX,
      rowId: row.id,
      trackId: getRowTrackId(row, visibleRows.length),
    })

    if (row.id === undefined || !expanded || activePath.has(row.id)) {
      return
    }

    activePath.add(row.id)
    for (const child of children) {
      appendVisibleRow(
        child,
        level + 1,
        columns,
        rowsByParentId,
        expandedRowIds,
        visibleRows,
        activePath,
      )
    }
    activePath.delete(row.id)
  }

  /** Groups rows by parent id while normalizing null parent links. */
  function getRowsByParentId(
    rows: readonly ents.core.display.Table.Tree.Row[],
  ): Map<
    ents.core.display.Table.Tree.Row.Id | undefined,
    ents.core.display.Table.Tree.Row[]
  > {
    const rowsByParentId = new Map<
      ents.core.display.Table.Tree.Row.Id | undefined,
      ents.core.display.Table.Tree.Row[]
    >()

    for (const row of rows) {
      const parentId = row.parentId ?? undefined
      const siblings = rowsByParentId.get(parentId) ?? []
      siblings.push(row)
      rowsByParentId.set(parentId, siblings)
    }

    return rowsByParentId
  }

  /** Finds rows that should be visible before any expansion happens. */
  function getRootRows(
    rows: readonly ents.core.display.Table.Tree.Row[],
    rowsByParentId: ReadonlyMap<
      ents.core.display.Table.Tree.Row.Id | undefined,
      readonly ents.core.display.Table.Tree.Row[]
    >,
    rootId?: ents.core.display.Table.Tree.Row.Id,
  ): readonly ents.core.display.Table.Tree.Row[] {
    if (rootId !== undefined) {
      return rowsByParentId.get(rootId) ?? []
    }

    const rowIds = new Set<ents.core.display.Table.Tree.Row.Id>()
    for (const row of rows) {
      if (row.id !== undefined) {
        rowIds.add(row.id)
      }
    }

    return rows.filter(
      row =>
        row.parentId === undefined ||
        row.parentId === null ||
        !rowIds.has(row.parentId),
    )
  }

  /** Creates one pre-filtered cell view model for template binding. */
  function getCellViewModel(
    row: ents.core.display.Table.Tree.Row,
    column: ColumnViewModel,
  ): CellViewModel {
    const value = getCellRawValue(row, column)

    return {
      columnId: column.id,
      tooltip: value !== undefined ? getCellTooltip(value) : '',
      value,
      valueInfo: column.valueInfo,
    }
  }

  /** Converts a row field to the raw value shape expected by the design value component. */
  function getCellRawValue(
    row: ents.core.display.Table.Tree.Row,
    { id, valueInfo }: ColumnViewModel,
  ): ents.core.common.value.Raw | undefined {
    const value = row.fields?.[id]?.value
    const type = valueInfo?.type

    if (type) {
      return ents.core.common.value.Raw.cast(value, type)
    }

    if (ents.core.common.value.Raw.isRaw(value)) {
      return value
    }

    return undefined
  }

  /** Tooltip text for truncated primitive cell values. */
  function getCellTooltip(value: ents.core.common.value.Raw): string {
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      return String(value)
    }

    if (value instanceof Date) {
      return value.toLocaleDateString()
    }

    return ''
  }

  /** Track expression for visible rows. */
  function getRowTrackId(
    row: ents.core.display.Table.Tree.Row,
    index: number,
  ): string {
    return row.id ?? `${row.parentId ?? 'root'}:${index}`
  }
}
